import {
  Button,
  ButtonColor,
  compact,
  FontWeight,
  formatName,
  formatPrice,
  IconChat,
  IconTest,
  ProfileImage,
  Rating,
  RatingSize,
  RatingTextColor,
  Tag,
  type TagFontWeight,
  TagSize,
} from '@leland-dev/leland-ui-library';
import Link from 'next/link';
import { type FC, type PropsWithChildren, useMemo } from 'react';

import {
  BadgeType,
  ExperienceType,
  GoalName,
  type Scalars,
  SchoolAdmissionStatus,
  type TestOutcomeSummary,
} from '../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../context/AuthContext';
import { LexSlug, useLex } from '../../context/LexContext';
import { getCoachUrl } from '../../utils/coach';
import { formatNumber } from '../../utils/common';
import useMediaQuery, { Breakpoints } from '../../utils/hooks/useMediaQuery';
import { useOnce } from '../../utils/hooks/useOnce';
import { useSessionState } from '../../utils/hooks/useSessionState';
import { useLoginModal } from '../LoginModal';
import { TierBadge } from '../TierBadge';
import AdditionalExperienceTag from '../coach/AdditionalExperienceTag';
import CompanyTag from '../coach/CompanyTag';
import { CustomerFavoriteTag } from '../coach/CustomerFavoriteTag';
import { SaveCoachButton } from '../coach/SaveCoachButton';
import SchoolTag from '../coach/SchoolTag';
import { SuperCoachTag } from '../coach/SuperCoachTag';
import { useMessageModal } from '../coach/modals/MessageModal';
import { SessionOption, useScheduleModal } from '../coach/modals/ScheduleModal';
import { type OrganizationTagDetails } from '../common/OrganizationTag';

import { useClientOutcomes } from './ClientOutcomes';
import {
  type CoachCardCategoryInformationFragment,
  type CoachCardCoachFragment,
  type CoachCardSkuFragment,
} from './__generated-gql-types__/CoachCard.generated';

interface CoachCardProps {
  coach: CoachCardCoachFragment;
  categoryId: Possible<Scalars['CategoryUrn']['output']>;
  selectedSchoolIds: Possible<Array<Scalars['SchoolUrn']['output']>>;
  selectedCompanyIds: Possible<Array<Scalars['CompanyUrn']['output']>>;
  className?: string;
  onClick?: () => void;
  skusData: Possible<CoachCardSkuFragment[]>;
  refetchHydratedCoaches?: () => void;
}

enum CTAClicked {
  MESSAGE = 'message',
  SCHEDULE = 'schedule',
}

interface PendingAction {
  type: CTAClicked;
  coach: CoachCardCoachFragment;
  categoryUrn: Nullable<Scalars['CategoryUrn']['output']>;
  categorySlug: Nullable<string>;
}

const CoachCard: React.FC<CoachCardProps> = ({
  coach,
  categoryId,
  selectedSchoolIds,
  selectedCompanyIds,
  className = '',
  onClick,
  skusData,
  refetchHydratedCoaches,
}) => {
  const isTablet = useMediaQuery(Breakpoints.TAB);

  const shouldShowCoachTier = false;

  const searchedCategoryInfo = coach.categoryInformationList?.find(
    // if no categoryId, return first result
    // otherwise find matching info
    (catInfo) => !categoryId || catInfo.category.id === categoryId,
  );

  const formattedCoachName = formatName({
    firstName: coach.user.firstName,
    lastName: coach.user.lastName,
  });

  const href = getCoachUrl({
    coachSlug: coach.slug,
    categorySlug: searchedCategoryInfo
      ? searchedCategoryInfo.category.slug
      : undefined,
  });

  return (
    <CoachCardContent
      coach={coach}
      selectedSchoolIds={selectedSchoolIds}
      selectedCompanyIds={selectedCompanyIds}
      className={className}
      onClick={onClick}
      href={href}
      formattedCoachName={formattedCoachName}
      shouldShowCoachTier={shouldShowCoachTier}
      searchedCategoryInfo={searchedCategoryInfo}
      isTablet={isTablet}
      skusData={skusData}
      refetchHydratedCoaches={refetchHydratedCoaches}
    />
  );
};

interface CoachCardContentProps {
  coach: CoachCardCoachFragment;
  selectedSchoolIds: Possible<Array<Scalars['SchoolUrn']['output']>>;
  selectedCompanyIds: Possible<Array<Scalars['CompanyUrn']['output']>>;
  className: string | undefined;
  onClick: (() => void) | undefined;
  href: string;
  formattedCoachName: string;
  shouldShowCoachTier: boolean;
  searchedCategoryInfo: Possible<CoachCardCategoryInformationFragment>;
  isTablet: Optional<boolean>;
  skusData: Possible<CoachCardSkuFragment[]>;
}

const CoachCardContent: FC<
  CoachCardContentProps & {
    refetchHydratedCoaches?: () => void;
  }
> = ({
  coach,
  selectedSchoolIds,
  selectedCompanyIds,
  className,
  onClick,
  href,
  formattedCoachName,
  shouldShowCoachTier,
  searchedCategoryInfo,
  isTablet,
  skusData,
  refetchHydratedCoaches,
}) => {
  const { currentUser } = useAuth();
  const { isLexEnabled } = useLex();
  const coachCanSetPrice = isLexEnabled(LexSlug.RAMP_COACH_SET_PRICE);
  const isCoachBadgesEnabled = isLexEnabled(LexSlug.COACH_BADGES);

  const isCustomerFavorite = useMemo(() => {
    return coach.badges?.some(
      (badge) =>
        badge.type === BadgeType.CUSTOMER_FAVORITE &&
        badge.categoryUrn === searchedCategoryInfo?.category.id,
    );
  }, [coach.badges, searchedCategoryInfo?.category.id]);

  const [pendingAction, setPendingAction] =
    useSessionState<PendingAction | null>({
      key: 'coach-card-pending-action',
    });

  const isSm = useMediaQuery(Breakpoints.SM);

  const { Modal: ScheduleModal, setOpen: setScheduleModalOpen } =
    useScheduleModal();
  const { setOpen: setLoginModalOpen, Modal: LoginModal } = useLoginModal();

  const { Modal: MessageModal, setOpen: setMessageModalOpen } =
    useMessageModal();

  const { Content: ClientOutcomes, hasOutcomes } = useClientOutcomes({
    coach,
    maxTags: isTablet ? 10 : 5,
    outcomeType: searchedCategoryInfo?.category.goal,
  });

  const testScore = useMemo(() => {
    const outcome = coach.outcomes?.find(
      (outcome) =>
        outcome.__typename === 'TestOutcomeSummary' &&
        outcome.category.id === searchedCategoryInfo?.category.id,
    ) as TestOutcomeSummary;
    return outcome?.scoreRange;
  }, [coach.outcomes, searchedCategoryInfo?.category.id]);

  const coachStartingPrice = useMemo(
    () =>
      (coachCanSetPrice
        ? coach.hourlyRate
        : skusData?.find((sku) => sku.tier === coach.tier)?.unitPrice) ?? null,
    [skusData, coach.tier, coachCanSetPrice, coach.hourlyRate],
  );

  const isLoggedIn = !!currentUser?.applicant;

  const hasRelationship = !!coach.coachingRelationship?.id;

  const HOURLY_AND_PACKAGES_INFO = (
    <div className="flex items-center justify-center gap-2">
      <SaveCoachButton
        coachUrn={coach.id}
        isSaved={!!coach.isSaved}
        categoryUrn={searchedCategoryInfo?.category.id ?? null}
      />
      {isSm && (hasRelationship || (coach.couldScheduleIntro && isLoggedIn)) ? (
        <Button
          id="coach-card--message-button"
          LeftIcon={IconChat}
          label=""
          hideLabel
          buttonColor={ButtonColor.WHITE}
          onClick={(e) => {
            e.preventDefault();
            if (isLoggedIn) {
              setMessageModalOpen(true);
            } else {
              setPendingAction({
                type: CTAClicked.MESSAGE,
                coach,
                categoryUrn: searchedCategoryInfo?.category.id ?? null,
                categorySlug: searchedCategoryInfo?.category.slug ?? null,
              });
              setLoginModalOpen(true);
            }
          }}
        />
      ) : null}

      {hasRelationship || (!coach.couldScheduleIntro && isLoggedIn) ? (
        <Button
          id="coach-card--view-profile-button"
          label="View profile"
          buttonColor={ButtonColor.WHITE}
          onClick={(e) => {
            e.preventDefault();
            window.open(
              getCoachUrl({
                coachSlug: coach.slug,
                categorySlug: searchedCategoryInfo?.category.slug,
              }),
            );
          }}
        />
      ) : (
        <Button
          id="coach-card--free-intro-call-button"
          label="Free intro call"
          buttonColor={ButtonColor.BLACK}
          onClick={(e) => {
            e.preventDefault();
            if (isLoggedIn) {
              setScheduleModalOpen(true);
            } else {
              setPendingAction({
                type: CTAClicked.SCHEDULE,
                coach,
                categoryUrn: searchedCategoryInfo?.category.id ?? null,
                categorySlug: searchedCategoryInfo?.category.slug ?? null,
              });
              setLoginModalOpen(true);
            }
          }}
        />
      )}
    </div>
  );

  // Handle post-authentication actions
  useOnce(
    () => {
      if (!pendingAction) return;
      if (pendingAction.type === CTAClicked.SCHEDULE) {
        setScheduleModalOpen(true);
      } else if (pendingAction.type === CTAClicked.MESSAGE) {
        setMessageModalOpen(true);
      }
      void refetchHydratedCoaches?.();
      setPendingAction(null);
    },
    !!isLoggedIn && !!pendingAction && pendingAction.coach.id === coach.id,
  );

  return (
    <div className={`overflow-hidden py-6 ${className}`}>
      <CardLink
        id="coach_card--coach_profile_link_v2"
        className="flex items-start justify-center hover:cursor-pointer"
        href={href}
        onClick={onClick}
      >
        <ProfileImage
          className="hidden size-28 shrink-0 !rounded-md md:flex"
          pictureLink={coach.user.pictureLink}
          firstName={coach.user.firstName}
          lastName={coach.user.lastName}
        />

        <div className="flex grow flex-col items-start justify-start gap-3 overflow-hidden md:ml-5">
          <div className="flex w-full flex-col justify-start gap-3 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start justify-between md:hidden">
              <ProfileImage
                className="mr-4 size-28 shrink-0 "
                pictureLink={coach.user.pictureLink}
                firstName={coach.user.firstName}
                lastName={coach.user.lastName}
                squareBorder
              />

              {HOURLY_AND_PACKAGES_INFO}
            </div>

            <div className="flex flex-col items-start justify-center gap-0.5">
              <div className="flex items-center justify-start">
                <p
                  className={`shrink-0 ${
                    coach.reviewTotal ? 'border-r' : ''
                  } pr-2 text-lg leading-4 text-leland-gray-light hover:cursor-pointer hover:underline`}
                >
                  {formattedCoachName}
                </p>
                {coach.reviewTotal ? (
                  <div className="line-clamp-1 flex items-center px-2 text-lg font-normal text-leland-gray-light">
                    <Rating
                      totalStars={1}
                      rate={coach.averageReviewRate}
                      showRate
                      size={RatingSize.MEDIUM}
                      textColor={RatingTextColor.DARK}
                    />

                    <p className="ml-1 line-clamp-1 text-leland-gray-extra-light">
                      ({coach.reviewTotal})
                    </p>
                  </div>
                ) : null}

                {shouldShowCoachTier ? (
                  <>
                    {/* only show divider if rating is also shown */}
                    {coach.reviewTotal ? (
                      <div className="size-0.5 rounded bg-leland-gray-light" />
                    ) : null}
                    <TierBadge
                      tier={coach.tier}
                      goal={searchedCategoryInfo?.category.goal}
                    />
                  </>
                ) : null}
                {coach.totalCoachingMinutes > 1000 ? (
                  <p className="border-l pl-2 text-lg font-normal leading-4 text-leland-gray-extra-light">
                    {formatNumber(coach.totalCoachingMinutes)} min coaching
                  </p>
                ) : null}
                {isCoachBadgesEnabled &&
                isCustomerFavorite &&
                searchedCategoryInfo?.category.name &&
                isSm ? (
                  <div className="ml-2">
                    <CustomerFavoriteTag
                      categoryName={searchedCategoryInfo?.category.name}
                    />
                  </div>
                ) : null}
              </div>
              {searchedCategoryInfo?.headline ? (
                <h2 className="line-clamp-2 break-words text-xl font-medium text-leland-gray-dark">
                  {searchedCategoryInfo?.headline}
                </h2>
              ) : null}
            </div>
            {isTablet ? HOURLY_AND_PACKAGES_INFO : null}
          </div>
          {searchedCategoryInfo?.category.goal === GoalName.TEST &&
          testScore ? null : hasOutcomes ? (
            <div className="flex items-center gap-2 text-lg text-leland-gray-extra-light">
              Successful clients at: <ClientOutcomes />
            </div>
          ) : null}

          {(searchedCategoryInfo?.qualifications ?? coach.bio) ? (
            <div className="mt-4 hidden break-words text-lg text-leland-gray-light md:m-0 md:line-clamp-2">
              {searchedCategoryInfo?.qualifications ?? coach.bio}
            </div>
          ) : null}
          <div className="flex w-full flex-col items-start justify-between gap-3 md:flex-row md:items-center md:gap-0">
            <OrganizationTags
              coach={coach}
              selectedSchoolIds={selectedSchoolIds}
              selectedCompanyIds={selectedCompanyIds}
              searchedCategoryInfo={searchedCategoryInfo}
              isV2
            />

            <div className="flex w-full items-center justify-between sm:w-auto">
              {coachStartingPrice ? (
                <p className="text-lg">
                  Starting at {formatPrice(coachStartingPrice)}
                  <span className="text-leland-gray-light">/hr</span>
                </p>
              ) : null}

              {isCoachBadgesEnabled &&
              isCustomerFavorite &&
              searchedCategoryInfo?.category.name &&
              !isSm ? (
                <div className="ml-2">
                  <CustomerFavoriteTag
                    categoryName={searchedCategoryInfo?.category.name}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </CardLink>
      <ScheduleModal
        coach={pendingAction?.coach ?? coach}
        categoryUrn={
          pendingAction?.categoryUrn ??
          searchedCategoryInfo?.category.id ??
          null
        }
        categorySlug={
          pendingAction?.categorySlug ??
          searchedCategoryInfo?.category.slug ??
          null
        }
        defaultSessionType={SessionOption.INTRO_CALL}
      />

      <MessageModal
        coach={pendingAction?.coach ?? coach}
        categoryUrn={
          pendingAction?.categoryUrn ??
          searchedCategoryInfo?.category.id ??
          null
        }
      />
      <LoginModal />
    </div>
  );
};

interface OrganizationTagsProps {
  coach: CoachCardCoachFragment;
  selectedSchoolIds: Possible<Array<Scalars['SchoolUrn']['output']>>;
  selectedCompanyIds: Possible<Array<Scalars['CompanyUrn']['output']>>;
  searchedCategoryInfo: Possible<CoachCardCategoryInformationFragment>;
  isV2?: boolean;
  className?: string;
}

const OrganizationTags: FC<OrganizationTagsProps> = ({
  coach,
  selectedSchoolIds,
  selectedCompanyIds,
  searchedCategoryInfo,
  isV2 = false,
  className,
}) => {
  const isTablet = useMediaQuery(Breakpoints.TAB);
  const { isLexEnabled } = useLex();
  const isCoachBadgesEnabled = isLexEnabled(LexSlug.COACH_BADGES);

  const additionalTagDetails: {
    size?: TagSize;
    fontWeight?: TagFontWeight;
  } | null = useMemo(() => {
    if (isV2) {
      return {
        size: TagSize.LARGE,
        fontWeight: FontWeight.NORMAL,
      };
    }
    return null;
  }, [isV2]);

  const coachProfessionalExperiences = coach.professionalExperiences;
  const companiesAdmitted = useMemo(
    () => coach.companiesAdmitted ?? [],
    [coach.companiesAdmitted],
  );
  const companyTagDetails: Nullable<OrganizationTagDetails> = useMemo(() => {
    const defaultCompany = coachProfessionalExperiences?.[0];
    const worksAt = selectedCompanyIds
      ? coachProfessionalExperiences?.find((experience) =>
          selectedCompanyIds.includes(experience.company.id ?? ''),
        )
      : null;
    const gotInto = selectedCompanyIds
      ? companiesAdmitted.find((company) =>
          selectedCompanyIds.includes(company.id ?? ''),
        )
      : null;
    const featuredCompany = coachProfessionalExperiences?.find(
      (company) => company.isFeatured,
    );

    const company =
      worksAt?.company ??
      gotInto ??
      featuredCompany?.company ??
      defaultCompany?.company;

    const baseWorksdAtText = isTablet
      ? `Work${
          (worksAt ?? featuredCompany ?? defaultCompany)?.endAt?.year
            ? 'ed'
            : 's'
        } at `
      : '';
    const baseGotIntoText = isTablet ? 'Got into ' : '';
    const text = worksAt?.company
      ? `${baseWorksdAtText}${worksAt.company.name}`
      : gotInto
        ? `${baseGotIntoText}${gotInto.name}`
        : (featuredCompany ?? defaultCompany)
          ? `${baseWorksdAtText}${
              (featuredCompany ?? defaultCompany)?.company.name
            }`
          : '';

    return company
      ? {
          organization: {
            name: company.name,
            logoLink: company.logoLink,
          },
          text,
          largeIcons: true,
          ...additionalTagDetails,
        }
      : null;
  }, [
    selectedCompanyIds,
    coachProfessionalExperiences,
    companiesAdmitted,
    isTablet,
    additionalTagDetails,
  ]);

  const coachEducationExperiences = coach.educationExperiences;
  const schoolsAdmittedTo = useMemo(
    () =>
      (coach.schoolAdmissionInfoList ?? []).filter(
        (schoolAdmissionInfo) =>
          schoolAdmissionInfo?.status === SchoolAdmissionStatus.ADMITTED,
      ),
    [coach.schoolAdmissionInfoList],
  );

  const schoolTagDetails: Nullable<OrganizationTagDetails> = useMemo(() => {
    const defaultSchool = coachEducationExperiences?.[0]?.school;
    const studiedAt = selectedSchoolIds
      ? coachEducationExperiences?.find((education) =>
          selectedSchoolIds.includes(education.school.id),
        )?.school
      : null;
    const admittedTo = selectedSchoolIds
      ? schoolsAdmittedTo.find((s) => selectedSchoolIds.includes(s.school.id))
          ?.school
      : null;
    const featuredSchool = coachEducationExperiences?.find(
      (education) => education.isFeatured,
    )?.school;

    const school = studiedAt ?? admittedTo ?? featuredSchool ?? defaultSchool;

    const baseStudiedAtText = isTablet ? 'Studied at ' : '';
    const baseAdmittedToText = isTablet ? 'Admitted to ' : '';
    const text = studiedAt
      ? `${baseStudiedAtText}${studiedAt.name}`
      : admittedTo
        ? `${baseAdmittedToText}${admittedTo.name}`
        : (featuredSchool ?? defaultSchool)
          ? `${baseStudiedAtText}${(featuredSchool ?? defaultSchool)?.name}`
          : '';

    return school
      ? {
          organization: {
            name: school.name,
            logoLink: school.logoLink,
          },
          text,
          largeIcons: true,
          ...additionalTagDetails,
        }
      : null;
  }, [
    selectedSchoolIds,
    coachEducationExperiences,
    schoolsAdmittedTo,
    isTablet,
    additionalTagDetails,
  ]);

  const hasEducationAdmissionsCommitteeExperience =
    coach.educationExperiences?.some(
      (education) => education.admissionCommittee,
    );
  const hasProfessionalAdmissionsCommitteeExperience =
    coach.professionalExperiences?.some(
      (experience) =>
        experience.experienceType === ExperienceType.ADMISSIONS_COMMITTEE,
    );
  const hasProfessionalHiringExperience = coach.professionalExperiences?.some(
    (experience) => experience.experienceType === ExperienceType.HIRING_MANAGER,
  );

  const tags: React.ReactNode[] = useMemo(() => {
    const tags: React.ReactNode[] = [];
    const schoolOrCompanyTag =
      searchedCategoryInfo?.category?.goal === GoalName.CAREER &&
      companyTagDetails?.organization ? (
        <CompanyTag
          key={`company-${companyTagDetails.organization.name}`}
          selectedCompanyTagDetails={companyTagDetails}
        />
      ) : schoolTagDetails?.organization ? (
        <SchoolTag
          key={`school-${schoolTagDetails.organization.name}`}
          selectedSchoolTagDetails={schoolTagDetails}
        />
      ) : null;

    if (coach.isSuperCoach && isCoachBadgesEnabled) {
      tags.push(<SuperCoachTag isLarge={isV2} />);
    }
    if (
      searchedCategoryInfo?.category.goal === GoalName.SCHOOL &&
      (hasEducationAdmissionsCommitteeExperience ||
        hasProfessionalAdmissionsCommitteeExperience)
    ) {
      tags.push(
        <AdditionalExperienceTag
          type={ExperienceType.ADMISSIONS_COMMITTEE}
          {...additionalTagDetails}
        />,
      );
    }
    if (
      searchedCategoryInfo?.category.goal === GoalName.TEST &&
      !!searchedCategoryInfo.testScore
    ) {
      tags.push(
        <Tag
          key={`${searchedCategoryInfo.testScore}-${searchedCategoryInfo.category.id}`}
          LeftIcon={IconTest}
          text={`${searchedCategoryInfo.testScore} on the ${searchedCategoryInfo.category.name}`}
          {...additionalTagDetails}
        />,
      );
    }
    tags.push(schoolOrCompanyTag);
    if (
      searchedCategoryInfo?.category.goal === GoalName.CAREER &&
      hasProfessionalHiringExperience
    ) {
      tags.push(
        <AdditionalExperienceTag
          type={ExperienceType.HIRING_MANAGER}
          {...additionalTagDetails}
        />,
      );
    }
    return tags.filter(compact);
  }, [
    searchedCategoryInfo?.category.goal,
    searchedCategoryInfo?.category.id,
    searchedCategoryInfo?.category.name,
    searchedCategoryInfo?.testScore,
    companyTagDetails,
    schoolTagDetails,
    coach.isSuperCoach,
    isCoachBadgesEnabled,
    hasEducationAdmissionsCommitteeExperience,
    hasProfessionalAdmissionsCommitteeExperience,
    hasProfessionalHiringExperience,
    isV2,
    additionalTagDetails,
  ]);

  const renderedTags = isV2 ? tags.slice(0, 2) : tags;
  const additionalTagsCount = tags.length - renderedTags.length;

  return (
    <div
      className={`flex flex-wrap items-center justify-start gap-2 ${className}`}
    >
      {renderedTags.map((tag) => tag)}
      {additionalTagsCount > 0 ? (
        <Link
          href={getCoachUrl({
            coachSlug: coach.slug,
            categorySlug: searchedCategoryInfo?.category.slug,
          })}
          className="underline"
          id={`coach-card--additional-tags`}
        >
          +{additionalTagsCount}
        </Link>
      ) : null}
    </div>
  );
};

type CardLinkProp = PropsWithChildren<{
  id: string;
  href: string;
  onClick?: () => void;
  className: string;
}>;

const CardLink: FC<CardLinkProp> = ({
  id,
  href,
  onClick,
  className,
  children,
}) => {
  return (
    <a
      className={className}
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={onClick}
      id={id}
    >
      {children}
    </a>
  );
};

export default CoachCard;
